/**
 * Barra de filtros del historial de pedidos (ADM-09). Todos se combinan entre
 * si y con el buscador; el control que esta filtrando se pinta en azul, igual
 * que en la tabla de productos.
 *
 * Las fechas son dias de Chile y se aplican al elegirlas: un `<input
 * type="date">` solo avisa el cambio cuando la fecha esta completa.
 */
import type { EstadoPedido, OrdenPedidosAdmin } from '@elcarpintero/compartido';

import {
  conCambio,
  ESTADOS_PEDIDO,
  OPCIONES_RANDOM,
  ORDENES_PEDIDOS,
} from '../../utilidades/filtros-pedidos';
import type { FiltrosPedidos as Filtros } from '../../utilidades/filtros-pedidos';
import { FiltroMonto } from './FiltroMonto';

/**
 * Textos cortos para que la barra quepa en una linea; el chip y la tabla usan
 * los largos de `textos-pedido.ts`.
 */
const TEXTO_PAGO: Record<EstadoPedido, string> = {
  pagado: 'Pagado',
  pendiente_pago: 'Sin pagar',
  rechazado: 'Rechazado',
  anulado: 'Anulado',
};

export interface FiltrosPedidosProps {
  filtros: Filtros;
  onCambiar(filtros: Filtros): void;
}

interface FiltroOpcionesProps<T extends string> {
  id: string;
  etiqueta: string;
  valor: T | undefined;
  todos: string;
  opciones: { valor: T; texto: string }[];
  onCambiar(valor: T | undefined): void;
}

/** Un `<select>` cuya primera opcion es "no filtrar". */
function FiltroOpciones<T extends string>({
  id,
  etiqueta,
  valor,
  todos,
  opciones,
  onCambiar,
}: FiltroOpcionesProps<T>) {
  return (
    <div className="filtro">
      <label className="filtro-et" htmlFor={id}>
        {etiqueta}
      </label>
      <select
        id={id}
        className={valor !== undefined ? 'selector activo' : 'selector'}
        value={valor ?? ''}
        onChange={(evento) => {
          const elegida = opciones.find((opcion) => opcion.valor === evento.target.value);
          onCambiar(elegida?.valor);
        }}
      >
        <option value="">{todos}</option>
        {opciones.map((opcion) => (
          <option key={opcion.valor} value={opcion.valor}>
            {opcion.texto}
          </option>
        ))}
      </select>
    </div>
  );
}

interface FiltroFechasProps {
  desde: string | undefined;
  hasta: string | undefined;
  onCambiar(desde: string | undefined, hasta: string | undefined): void;
}

function valorDia(texto: string): string | undefined {
  return texto.length > 0 ? texto : undefined;
}

/** Desde y hasta bajo un solo rotulo, como el rango del total. */
function FiltroFechas({ desde, hasta, onCambiar }: FiltroFechasProps) {
  const clase = (valor: string | undefined) =>
    valor !== undefined ? 'entrada entrada-fecha activo' : 'entrada entrada-fecha';

  return (
    <div className="filtro filtro-stock">
      <span className="filtro-et" id="filtro-fecha-et">
        Fecha
      </span>
      <div className="rango-stock" role="group" aria-labelledby="filtro-fecha-et">
        <label>
          <span className="solo-lectores">Desde</span>
          <input
            type="date"
            className={clase(desde)}
            value={desde ?? ''}
            max={hasta}
            onChange={(evento) => {
              onCambiar(valorDia(evento.target.value), hasta);
            }}
          />
        </label>
        <span aria-hidden="true">a</span>
        <label>
          <span className="solo-lectores">Hasta</span>
          <input
            type="date"
            className={clase(hasta)}
            value={hasta ?? ''}
            min={desde}
            onChange={(evento) => {
              onCambiar(desde, valorDia(evento.target.value));
            }}
          />
        </label>
      </div>
    </div>
  );
}

export function FiltrosPedidos({ filtros, onCambiar }: FiltrosPedidosProps) {
  const cambiar = (cambio: Partial<Filtros>) => {
    onCambiar(conCambio(filtros, cambio));
  };

  /** Un rango escrito al reves se da vuelta en vez de dejar la tabla vacia. */
  function cambiarFechas(desde: string | undefined, hasta: string | undefined): void {
    if (desde !== undefined && hasta !== undefined && desde > hasta) {
      cambiar({ desde: hasta, hasta: desde });
    } else {
      cambiar({ desde, hasta });
    }
  }

  return (
    <section className="filtros-tabla" aria-label="Filtros de pedidos">
      <FiltroOpciones
        id="filtro-estado-pago"
        etiqueta="Pago"
        valor={filtros.estado}
        todos="Todos"
        opciones={ESTADOS_PEDIDO.map((estado) => ({ valor: estado, texto: TEXTO_PAGO[estado] }))}
        onCambiar={(estado) => {
          cambiar({ estado });
        }}
      />

      <FiltroOpciones
        id="filtro-random"
        etiqueta="Random"
        valor={filtros.random}
        todos="Todos"
        opciones={OPCIONES_RANDOM}
        onCambiar={(random) => {
          cambiar({ random });
        }}
      />

      <FiltroOpciones
        id="filtro-entrega"
        etiqueta="Entrega"
        valor={filtros.tipoEntrega}
        todos="Todas"
        opciones={[
          { valor: 'despacho', texto: 'Despacho' },
          { valor: 'retiro', texto: 'Retiro' },
        ]}
        onCambiar={(tipoEntrega) => {
          cambiar({ tipoEntrega });
        }}
      />

      <FiltroOpciones
        id="filtro-documento"
        etiqueta="Documento"
        valor={filtros.tipoDocumento}
        todos="Todos"
        opciones={[
          { valor: 'boleta', texto: 'Boleta' },
          { valor: 'factura', texto: 'Factura' },
        ]}
        onCambiar={(tipoDocumento) => {
          cambiar({ tipoDocumento });
        }}
      />

      <FiltroFechas desde={filtros.desde} hasta={filtros.hasta} onCambiar={cambiarFechas} />

      <FiltroMonto
        que="Total"
        minimo={filtros.totalMin}
        maximo={filtros.totalMax}
        onCambiar={({ minimo, maximo }) => {
          cambiar({ totalMin: minimo, totalMax: maximo });
        }}
      />

      <div className="filtro filtro-orden">
        <label className="filtro-et" htmlFor="filtro-orden-pedidos">
          Ordenar por
        </label>
        <select
          id="filtro-orden-pedidos"
          className="selector"
          value={filtros.orden ?? 'recientes'}
          onChange={(evento) => {
            const orden = evento.target.value as OrdenPedidosAdmin;
            cambiar({ orden: orden === 'recientes' ? undefined : orden });
          }}
        >
          {ORDENES_PEDIDOS.map((opcion) => (
            <option key={opcion.valor} value={opcion.valor}>
              {opcion.texto}
            </option>
          ))}
        </select>
      </div>
    </section>
  );
}
